/* eslint-disable indent */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { IoIosArrowDown } from 'react-icons/io';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Stack,
  Text,
  useToast,
  Icon,
  AccordionPanel,
  Select,
  Spinner,
  Textarea,
  Flex,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  updateSymptom,
  useAppDispatch,
  useLazyListServicesQuery,
} from '@spartalabs/pdc-core';
import { generateId } from 'utils/functions';
import * as z from 'zod';

const SelectLoading = () => {
  return <Spinner size="sm" color="gray.100" />;
};

const ServiceFormData = z.object({
  details: z.array(z.string()).optional(),
  observation: z.string().optional(),
});

type ServiceFormSchema = z.infer<typeof ServiceFormData>;

interface ServiceProps {
  service?: ScheduleSymptom;
  hasDivider?: boolean;
  handleAddService: (service: ScheduleSymptom) => void;
}

const Service = forwardRef<ScheduleSymptom, ServiceProps>(
  ({ service, hasDivider, handleAddService }, ref) => {
    const dispatch = useAppDispatch();
    const toast = useToast();
    const currentId = useRef<string>(generateId());

    const [fetchServices, { isLoading, isFetching }] =
      useLazyListServicesQuery();

    const { register, handleSubmit, setValue, getValues, watch } =
      useForm<ServiceFormSchema>({
        resolver: zodResolver(ServiceFormData),
      });

    const [currentService, setCurrentService] = useState(service);
    useEffect(() => {
      if (currentService?.id !== service?.id) {
        setCurrentService(service);
        resetForm();
        setValue('observation', service?.observation);
        setFirstLevelServices(service?.firstLevelOptions);
        setSecondLevelServices(
          service?.details?.[0] ? service?.secondLevelOptions : [],
        );
        setThirdLevelServices(
          service?.details?.[1] ? service?.thirdLevelOptions : [],
        );
        setFourthLevelServices(
          service?.details?.[2] ? service?.fourthLevelOptions : [],
        );
      }
    }, [service]);

    const [firstLevelServices, setFirstLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.firstLevelOptions || undefined);
    const [secondLevelServices, setSecondLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.secondLevelOptions || undefined);
    const [thirdLevelServices, setThirdLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.thirdLevelOptions || undefined);
    const [fourthLevelServices, setFourthLevelServices] = useState<
      ScheduleService[] | undefined
    >(currentService?.fourthLevelOptions || undefined);

    const [firstId, setFirstId] = useState<ScheduleSymptomDetail | undefined>();
    const [secondId, setSecondId] = useState<
      ScheduleSymptomDetail | undefined
    >();
    const [thirdId, setThirdId] = useState<ScheduleSymptomDetail | undefined>();
    const [fourthId, setFourthId] = useState<
      ScheduleSymptomDetail | undefined
    >();

    useImperativeHandle(
      ref,
      () => {
        const data = getValues();
        const details = data.details?.map(detail => {
          if (detail && detail !== '') return JSON.parse(detail);
        });
        const observation = data.observation;

        const notAddedService = { details, observation, id: currentId.current };

        currentId.current = generateId();

        return notAddedService;
      },
      [
        watch('details.0'),
        watch('details.1'),
        watch('details.2'),
        watch('details.3'),
        watch('observation'),
      ],
    );

    useEffect(() => {
      if (currentService) {
        if (currentService?.details?.[0]) {
          setValue('details.0', JSON.stringify(currentService.details[0]));
        }

        if (currentService?.details?.[1]) {
          setValue('details.1', JSON.stringify(currentService.details[1]));
        }

        if (currentService?.details?.[2]) {
          setValue('details.2', JSON.stringify(currentService.details[2]));
        }

        if (currentService?.details?.[3]) {
          setValue('details.3', JSON.stringify(currentService.details[3]));
        }
      } else {
        fetchFirstLevel();
      }
    }, []);

    useEffect(() => {
      if (currentService) {
        if (currentService?.details?.[0]) {
          setValue('details.0', JSON.stringify(currentService.details[0]));
        }

        if (currentService?.details?.[1]) {
          setValue('details.1', JSON.stringify(currentService.details[1]));
        }

        if (currentService?.details?.[2]) {
          setValue('details.2', JSON.stringify(currentService.details[2]));
        }

        if (currentService?.details?.[3]) {
          setValue('details.3', JSON.stringify(currentService.details[3]));
        }
      }
    }, [currentService]);

    useEffect(() => {
      if (service && currentService?.id === service.id) {
        dispatch(
          updateSymptom({
            id: service.id,
            details: currentService?.details?.filter(
              detail => detail !== undefined,
            ),
            observation: watch('observation'),
            firstLevelOptions: firstLevelServices,
            secondLevelOptions: secondLevelServices,
            thirdLevelOptions: thirdLevelServices,
            fourthLevelOptions: fourthLevelServices,
          }),
        );
      }
    }, [
      watch('details.0'),
      watch('details.1'),
      watch('details.2'),
      watch('details.3'),
      watch('observation'),
    ]);

    const resetForm = () => {
      setFirstId(undefined);
      setSecondId(undefined);
      setThirdId(undefined);
      setFourthId(undefined);
      setSecondLevelServices([]);
      setThirdLevelServices([]);
      setFourthLevelServices([]);
      setValue('observation', '');
      setValue('details.0', '');
      setValue('details.1', '');
      setValue('details.2', '');
      setValue('details.3', '');
    };

    const fetchFirstLevel = useCallback(async () => {
      try {
        const firstLevelServices = await fetchServices({
          firstLevel: ' ',
        }).unwrap();

        setFirstLevelServices(firstLevelServices);
      } catch (error) {
        toast({
          title: 'Algo deu errado!',
          position: 'top',
          description: 'Tente novamente mais tarde',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      }
    }, []);

    const fetchSecondSelect = useCallback(
      async (
        services: ScheduleSymptomDetail[],
        currentServiceAux?: ScheduleSymptom,
      ) => {
        const service = services[0];
        setValue('details.0', JSON.stringify(service));

        try {
          const secondLevelServices = await fetchServices({
            firstLevel: service?.id,
          }).unwrap();

          setFirstId(service);
          setValue('details.0', JSON.stringify(service));

          setSecondLevelServices(secondLevelServices);
          if (currentServiceAux?.details?.[1]) {
            setValue('details.1', JSON.stringify(currentServiceAux.details[1]));
          }
        } catch (error) {
          toast({
            title: 'Algo deu errado!',
            position: 'top',
            description:
              'Tente novamente mais tarde ou selecione outro serviço',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
      },
      [firstLevelServices, currentService],
    );

    const fetchThirdSelect = useCallback(
      async (
        services: ScheduleSymptomDetail[],
        currentServiceAux?: ScheduleSymptom,
      ) => {
        const service = services[1];
        try {
          const thirdLevelServices = await fetchServices({
            firstLevel: services[0].id,
            secondLevel: service.id,
          }).unwrap();

          setSecondId(service);

          setThirdLevelServices(thirdLevelServices);
          if (currentServiceAux?.details?.[2]) {
            setValue('details.2', JSON.stringify(currentServiceAux.details[2]));
          }
        } catch (error) {
          toast({
            title: 'Algo deu errado!',
            position: 'top',
            description:
              'Tente novamente mais tarde ou selecione outro serviço',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
      },
      [secondLevelServices, currentService],
    );

    const fetchFourthSelect = useCallback(
      async (
        services: ScheduleSymptomDetail[],
        currentServiceAux?: ScheduleSymptom,
      ) => {
        const service = services[2];

        try {
          const fourthLevelServices = await fetchServices({
            firstLevel: services[0]?.id,
            secondLevel: services[1]?.id,
            thirdLevel: service?.id,
          }).unwrap();

          setThirdId(service);

          setFourthLevelServices(fourthLevelServices);
          if (currentServiceAux?.details?.[3]) {
            setValue('details.3', JSON.stringify(currentServiceAux.details[3]));
          }
        } catch (error) {
          toast({
            title: 'Algo deu errado!',
            position: 'top',
            description:
              'Tente novamente mais tarde ou selecione outro serviço',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
      },
      [thirdLevelServices, currentService],
    );

    const onSubmit = (data: ServiceFormSchema) => {
      const details = data.details?.map(detail => {
        if (detail && detail !== '') return JSON.parse(detail);
      });
      const observation = data.observation;

      handleAddService({
        details,
        observation,
        id: currentId.current,
        firstLevelOptions: firstLevelServices,
        secondLevelOptions: secondLevelServices,
        thirdLevelOptions: thirdLevelServices,
        fourthLevelOptions: fourthLevelServices,
      });

      currentId.current = generateId();

      resetForm();
    };

    return (
      <Accordion defaultIndex={-1} allowToggle gap={0} w="100%">
        <AccordionItem
          isDisabled={
            service && (isLoading || isFetching) && !secondLevelServices
          }
          py="4px"
          borderTop="transparent"
          borderBottomColor={hasDivider ? undefined : 'transparent'}
        >
          {({ isExpanded }) => (
            <>
              <AccordionButton _hover={{ bg: 'transparent' }}>
                <Box as="span" flex="1" textAlign="left" color="black">
                  <Text color="text.100">
                    {currentService
                      ? currentService?.details?.[0]?.name ||
                        'Serviço não definido'
                      : 'Selecione o serviço'}
                  </Text>
                </Box>
                {service &&
                (isLoading || isFetching) &&
                !secondLevelServices ? (
                  <SelectLoading />
                ) : currentService ? (
                  <Icon
                    as={IoIosArrowDown}
                    color="text.100"
                    transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                  />
                ) : (
                  <Icon
                    as={isExpanded ? MinusIcon : AddIcon}
                    color="text.100"
                    transition="all .5s ease"
                    h="12px"
                    w="12px"
                  />
                )}
              </AccordionButton>
              <AccordionPanel display="flex" flexDir="column" gap="20px">
                <Select
                  size="lg"
                  bg="transparent"
                  border="1px solid"
                  borderColor="gray.100"
                  color="text.100"
                  fontSize="16px"
                  fontWeight="500"
                  placeholder="Selecionar..."
                  {...register('details.0', {
                    onChange: () => {
                      setFirstId(undefined);
                      setSecondId(undefined);
                      setThirdId(undefined);
                      setFourthId(undefined);
                      setSecondLevelServices(undefined);
                      setThirdLevelServices(undefined);
                      setFourthLevelServices(undefined);
                      setValue('details.1', '');
                      setValue('details.2', '');
                      setValue('details.3', '');
                      const services = getValues('details');
                      const parsedServices = services?.map(service => {
                        if (service && service !== '')
                          return JSON.parse(service);
                      });
                      if (service) {
                        setCurrentService(prev => {
                          const newCurrentService = {
                            ...prev,
                            details: parsedServices
                              ? [parsedServices[0]]
                              : undefined,
                          };

                          fetchSecondSelect(
                            newCurrentService.details as ScheduleSymptomDetail[],
                            newCurrentService,
                          );

                          return newCurrentService;
                        });
                      } else {
                        fetchSecondSelect(
                          parsedServices as ScheduleSymptomDetail[],
                          currentService,
                        );
                      }
                    },
                  })}
                  icon={
                    (isLoading || isFetching) && !firstId ? (
                      <SelectLoading />
                    ) : undefined
                  }
                  isDisabled={(isLoading || isFetching) && !firstId}
                >
                  {firstLevelServices?.map((state, index: number) => (
                    <option key={index} value={JSON.stringify(state)}>
                      {state.name}
                    </option>
                  ))}
                </Select>

                {secondLevelServices && secondLevelServices.length > 1 && (
                  <Select
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="gray.100"
                    color="text.100"
                    placeholder="Selecionar..."
                    fontSize="16px"
                    fontWeight="500"
                    {...register('details.1', {
                      onChange: () => {
                        setSecondId(undefined);
                        setThirdId(undefined);
                        setFourthId(undefined);
                        setThirdLevelServices(undefined);
                        setFourthLevelServices(undefined);
                        setValue('details.2', '');
                        setValue('details.3', '');
                        const services = getValues('details');
                        const parsedServices = services?.map(service => {
                          if (service && service !== '')
                            return JSON.parse(service);
                        });
                        if (service) {
                          setCurrentService(prev => {
                            const newCurrentService = {
                              ...prev,
                              details: parsedServices
                                ? [parsedServices[0], parsedServices[1]]
                                : undefined,
                            };

                            fetchThirdSelect(
                              newCurrentService.details as ScheduleSymptomDetail[],
                              newCurrentService,
                            );

                            return newCurrentService;
                          });
                        } else {
                          fetchThirdSelect(
                            parsedServices as ScheduleSymptomDetail[],
                            currentService,
                          );
                        }
                      },
                    })}
                    icon={
                      (isLoading || isFetching) && !secondId ? (
                        <SelectLoading />
                      ) : undefined
                    }
                    isDisabled={(isLoading || isFetching) && !secondId}
                  >
                    {secondLevelServices.map((state, index: number) => (
                      <option key={index} value={JSON.stringify(state)}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}

                {thirdLevelServices && thirdLevelServices.length > 1 && (
                  <Select
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="gray.100"
                    placeholder="Selecionar..."
                    color="text.100"
                    fontSize="16px"
                    fontWeight="500"
                    {...register('details.2', {
                      onChange: () => {
                        setThirdId(undefined);
                        setFourthId(undefined);
                        setFourthLevelServices(undefined);
                        setValue('details.3', '');
                        const services = getValues('details');
                        const parsedServices = services?.map(service => {
                          if (service && service !== '')
                            return JSON.parse(service);
                        });
                        if (service) {
                          setCurrentService(prev => {
                            const newCurrentService = {
                              ...prev,
                              details: parsedServices
                                ? [
                                    parsedServices[0],
                                    parsedServices[1],
                                    parsedServices[2],
                                  ]
                                : undefined,
                            };

                            fetchFourthSelect(
                              newCurrentService.details as ScheduleSymptomDetail[],
                              newCurrentService,
                            );

                            return newCurrentService;
                          });
                        } else {
                          fetchFourthSelect(
                            parsedServices as ScheduleSymptomDetail[],
                            currentService,
                          );
                        }
                      },
                    })}
                    icon={
                      (isLoading || isFetching) && !thirdId ? (
                        <SelectLoading />
                      ) : undefined
                    }
                    isDisabled={(isLoading || isFetching) && !thirdId}
                  >
                    {thirdLevelServices.map((state, index: number) => (
                      <option key={index} value={JSON.stringify(state)}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}

                {fourthLevelServices && fourthLevelServices.length > 1 && (
                  <Select
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="gray.100"
                    placeholder="Selecionar..."
                    color="text.100"
                    fontSize="16px"
                    fontWeight="500"
                    {...register('details.3', {
                      onChange: () => {
                        setFourthId(undefined);
                        const services = getValues('details');
                        const parsedServices = services?.map(service => {
                          if (service && service !== '')
                            return JSON.parse(service);
                        });
                        if (service)
                          setCurrentService(prev => {
                            const newCurrentService = {
                              ...prev,
                              details: parsedServices
                                ? [
                                    parsedServices[0],
                                    parsedServices[1],
                                    parsedServices[2],
                                  ]
                                : undefined,
                            };

                            return newCurrentService;
                          });
                      },
                    })}
                    icon={
                      (isLoading || isFetching) && !fourthId ? (
                        <SelectLoading />
                      ) : undefined
                    }
                    isDisabled={(isLoading || isFetching) && !fourthId}
                  >
                    {fourthLevelServices.map((state, index: number) => (
                      <option key={index} value={JSON.stringify(state)}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}

                <Stack>
                  <Text as="label" fontSize="14px" color="text.300">
                    Adicione detalhes da solicitação
                  </Text>
                  <Textarea
                    variant="unstyled"
                    bg="transparent"
                    border="1px solid"
                    borderColor="gray.100"
                    color="text.100"
                    fontSize="16px"
                    fontWeight="500"
                    defaultValue={currentService?.observation}
                    {...register('observation')}
                    p="3"
                    size="lg"
                  />
                </Stack>
                <Flex justify="end">
                  {!currentService && (
                    <AccordionButton
                      gap="4px"
                      textDecor="none"
                      onClick={handleSubmit(onSubmit)}
                      justifyContent="center"
                    >
                      <Text
                        fontSize="14px"
                        fontWeight="normal"
                        color="brazil.km"
                      >
                        Adicionar mais serviços
                      </Text>
                      <Icon
                        as={AiOutlinePlusCircle}
                        h="16px"
                        w="16px"
                        color="linkBlue"
                      />
                    </AccordionButton>
                  )}
                </Flex>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    );
  },
);

Service.displayName = 'Service';

export default Service;
